import React, { useState, useEffect, useRef, useCallback } from 'react';
import { htmlToMd } from './lib/htmlToMd';
import { NotesMarkdown } from './components/NotesMarkdown';
import { downloadMarkdown, downloadMarkdownZip, toMarkdownFileName } from './lib/downloadMarkdown';
import { PlusCircle, XCircle, ClipboardList, MoreVertical, Edit2, LogOut, FileText, Star, Circle, HelpCircle, Settings, CreditCard, Menu, X, Trash2, Send, Inbox, Share2, Columns, Calendar, ChevronDown, ChevronUp, FolderOpen, Download, Archive } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { supabase } from './lib/supabase';
import { useAuth } from './lib/AuthContext';
import { useTranslation } from './lib/LanguageContext';
import { AuthForm } from './components/AuthForm';
import { LandingPage } from './components/LandingPage';
import TodoEditText from './components/TodoEditText';
import { NotesEditor } from './components/NotesEditor';
import { ProjectSelector } from './components/ProjectSelector';
import { AISuggestions } from './components/AISuggestions';
import { getUserSubscription, SubscriptionDetails, getPolarCustomer, createPolarPortalSession } from './lib/subscription';
import { SubscriptionBanner } from './components/SubscriptionBanner';
import { SubscriptionModal } from './components/SubscriptionModal';
import { AccountSettings } from './components/AccountSettings';
import { AIUsageIndicator } from './components/AIUsageIndicator';
import { SendStickyNoteModal } from './components/SendStickyNoteModal';
import { SharedInbox } from './components/SharedInbox';
import { SharedOutbox } from './components/SharedOutbox';
import { ColumnSettings, ColumnSettingsButton } from './components/ColumnSettings';
import { SetDeadlineModal } from './components/SetDeadlineModal';
import { MoveToProjectModal } from './components/MoveToProjectModal';
import { Footer } from './components/Footer';
import { CookieConsent } from './components/CookieConsent';
import { HelpButton } from './components/HelpButton';
import { takePendingOAuthConsent } from './lib/oauthConsentReturn';
// import { SentryTest } from './components/SentryTest'; // Only for development testing

type TodoColor = 'yellow' | 'green' | 'blue' | null;

interface Project {
  id: string;
  name: string;
  user_id: string;
}

interface Todo {
  id: string;
  text: string;
  completed: boolean;
  status: 'backlog' | 'in_progress' | 'review' | 'testing' | 'done';
  user_id: string;
  project_id: string;
  notes: string | null;
  priority: boolean;
  color: TodoColor;
  completed_at: string | null;
  position: number;
  deadline: string | null;
}

type ColumnType = 'backlog' | 'in_progress' | 'review' | 'testing' | 'done';

function App() {
  const { user, loading: authLoading, signOut } = useAuth();
  const { t } = useTranslation();
  const [todos, setTodos] = useState<Todo[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{id: string, y: number} | null>(null);
  const [editingTodo, setEditingTodo] = useState<{ id: string; text: string } | null>(null);
  const [editingNotes, setEditingNotes] = useState<{ id: string; notes: string } | null>(null);
  const [showingNotes, setShowingNotes] = useState<string | null>(null);
  const [editingDeadline, setEditingDeadline] = useState<{ id: string; deadline: string | null } | null>(null);
  const [movingTodo, setMovingTodo] = useState<Todo | null>(null);
  const [showMoveToProjectModal, setShowMoveToProjectModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [polarCustomer, setPolarCustomer] = useState<{
    polar_customer_id: string,
    subscription_id: string,
    subscription_status: string,
    cancel_at_period_end: boolean
  } | null>(null);
  const [showSubscriptionBanner, setShowSubscriptionBanner] = useState(true);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{ id: string; text: string } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  
  // New states for collaboration features
  const [sendingStickyNote, setSendingStickyNote] = useState<Todo | null>(null);
  const [showInbox, setShowInbox] = useState(false);
  const [showOutbox, setShowOutbox] = useState(false);
  const [inboxCount, setInboxCount] = useState(0);
  
  // New state for column settings
  const [columnSettings, setColumnSettings] = useState<ColumnSettings>({
    backlog: true,
    in_progress: true,
    review: false,
    testing: false,
    done: true
  });
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  // State for mobile accordion - tracks which columns are collapsed
  const [collapsedColumns, setCollapsedColumns] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        activeMenu &&
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setActiveMenu(null);
        setMenuPosition(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeMenu]);

  // Back to an OAuth consent request interrupted by Google sign-in, which
  // always returns to the app root
  useEffect(() => {
    if (!user) return;
    const consentUrl = takePendingOAuthConsent();
    if (consentUrl) window.location.replace(consentUrl);
  }, [user]);

  // SECURITY: Clear all user data when user logs out (user becomes null)
  // This prevents the next user from seeing the previous user's data
  useEffect(() => {
    if (!user) {
      // Clear all user-specific data
      setTodos([]);
      setProjects([]);
      setSelectedProject(null);
      setSubscription(null);
      setPolarCustomer(null);
      setInboxCount(0);

      // Reset user settings to defaults
      setColumnSettings({
        backlog: true,
        in_progress: true,
        review: false,
        testing: false,
        done: true
      });

      // Clear UI state
      setInput('');
      setShowingNotes(null);
      setEditingTodo(null);
      setEditingNotes(null);
      setEditingDeadline(null);
      setActiveMenu(null);
      setMenuPosition(null);

      // Clear collaboration state
      setSendingStickyNote(null);
      setShowInbox(false);
      setShowOutbox(false);

      // Close all modals
      setShowLogoutConfirm(false);
      setShowSubscriptionModal(false);
      setShowAccountSettings(false);
      setShowColumnSettings(false);
      setShowDeleteAllConfirm(false);
      setShowDeleteConfirm(null);

      // Reset mobile UI state
      setMobileMenuOpen(false);
      setCollapsedColumns({});

      // Set loading to true to prevent flash of old data
      setLoading(true);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchSubscription();
      fetchPolarCustomer();
    }
  }, [user]);
  
  // Check for shared sticky notes
  useEffect(() => {
    if (user) {
      fetchInboxCount();
    }
  }, [user]);
  
  // Refresh inbox count on a timer
  useEffect(() => {
    if (user) {
      const interval = setInterval(fetchInboxCount, 60000); // Check every minute
      return () => clearInterval(interval);
    }
  }, [user]);
  
  // Load user settings
  useEffect(() => {
    if (user) {
      fetchUserSettings();
    }
  }, [user]);

  const fetchUserSettings = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase.rpc('get_user_settings');
      
      if (error) {
        console.error('Error fetching user settings:', error);
        return;
      }
      
      const settings = data as unknown as { columns?: ColumnSettings } | null;
      if (settings?.columns) {
        setColumnSettings(settings.columns);
      }
    } catch (err) {
      console.error('Error fetching user settings:', err);
    }
  };

  const handleColumnSettingsChange = (newSettings: ColumnSettings) => {
    setColumnSettings(newSettings);
  };

  const fetchPolarCustomer = async () => {
    if (!user) return;

    try {
      const customer = await getPolarCustomer();
      setPolarCustomer(customer);
    } catch (err) {
      console.error('Error loading Polar customer:', err);
    }
  };

  const fetchInboxCount = async () => {
    if (!user?.email) return;
    
    try {
      // First check that we have an authenticated session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        console.error('Session error:', sessionError);
        setInboxCount(0);
        return;
      }
      
      if (!sessionData.session) {
        console.log('No active session found');
        setInboxCount(0);
        return;
      }
      
      // Make sure we're using lowercase email to match RLS policy
      const userEmail = user.email.toLowerCase();
      
      const { count, error } = await supabase
        .from('shared_todos')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_email', userEmail)
        .eq('status', 'pending');
      
      if (error) {
        console.error('Error fetching inbox count:', error);
        setInboxCount(0);
        return;
      }
      
      setInboxCount(count || 0);
    } catch (err) {
      console.error('Error fetching inbox count:', err);
      // Don't show an alert as this error might happen silently
      setInboxCount(0);
    }
  };

  const fetchSubscription = async () => {
    try {
      // Add a timeout to make sure we don't wait forever
      const abortController = new AbortController();
      const timeout = setTimeout(() => abortController.abort(), 10000);
      
      const data = await getUserSubscription();
      clearTimeout(timeout);
      
      if (data) {
        setSubscription(data);
      } else {
        // If data is null, set a fallback subscription
        setSubscription({
          subscriptionTier: 'free',
          taskLimit: 50,
          currentTaskCount: 0,
          subscriptionExpiresAt: null,
          percentageUsed: 0,
          isWithinLimit: true,
          daysRemaining: null
        });
        console.log('Using fallback subscription data');
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      // Set fallback subscription data on error
      setSubscription({
        subscriptionTier: 'free',
        taskLimit: 50,
        currentTaskCount: 0,
        subscriptionExpiresAt: null,
        percentageUsed: 0,
        isWithinLimit: true,
        daysRemaining: null
      });
    }
  };

  const handleSubscriptionSuccess = async () => {
    await fetchSubscription();
    await fetchPolarCustomer();
    setShowSubscriptionBanner(true);
  };

  const handleManageSubscription = async () => {
    if (!polarCustomer?.polar_customer_id) {
      // If no Polar customer exists, open subscription modal for new checkout
      setShowSubscriptionModal(true);
      return;
    }

    try {
      // Open Polar Customer Portal
      const currentOrigin = window.location.origin;
      const url = await createPolarPortalSession(user!.id, currentOrigin);
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('Failed to create portal session');
      }
    } catch (err) {
      console.error('Error opening Polar portal:', err);
      alert('Failed to open subscription management. Please try again or contact support.');
    }
  };

  const hasNotes = (notes: string | null | undefined): boolean => {
    if (!notes || typeof notes !== 'string') return false;
    return notes.trim().length > 0;
  };

  const fetchProjects = async () => {
    if (!user) return;
    
    try {
      // Check if Supabase is properly initialized
      if (!supabase || typeof supabase.from !== 'function') {
        throw new Error('Supabase client is not properly initialized');
      }
      
      // First test the connection with a simple health check
      const { error: connectionError } = await supabase.from('projects').select('count').limit(1);
      if (connectionError) {
        throw new Error(`Connection error: ${connectionError.message}`);
      }
      
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setProjects(data || []);
      
      if (!selectedProject && data && data.length > 0) {
        setSelectedProject(data[0].id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      // Show an alert with a user-friendly error message
      alert(`Unable to connect to the server. Please check your internet connection and try again.`);
    }
  };

  const fetchTodos = async () => {
    if (!user || !selectedProject) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('todos')
        .select('*')
        .eq('user_id', user.id)
        .eq('project_id', selectedProject)
        .order('position', { ascending: true });

      if (error) throw error;
      const sorted = ((data || []) as Todo[]).sort((a, b) => {
        if (a.priority === b.priority) return a.position - b.position;
        return a.priority ? -1 : 1;
      });
      setTodos(sorted);
    } catch (error) {
      console.error('Error fetching sticky notes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchProjects();
    }
  }, [user]);

  useEffect(() => {
    if (selectedProject) {
      fetchTodos();
    } else {
      setTodos([]);
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => {
    const handleRefreshTodos = () => {
      fetchTodos();
    };

    window.addEventListener('refreshTodos', handleRefreshTodos);
    return () => window.removeEventListener('refreshTodos', handleRefreshTodos);
  }, [selectedProject]);

  useEffect(() => {
    const handleOpenAccountSettings = () => {
      setShowAccountSettings(true);
    };

    window.addEventListener('openAccountSettings', handleOpenAccountSettings);
    return () => window.removeEventListener('openAccountSettings', handleOpenAccountSettings);
  }, []);

  const addTodo = async (text: string) => {
    if (!text.trim() || !user || !selectedProject) return;

    try {
      // Check if user is within sticky note limit
      if (subscription && !subscription.isWithinLimit) {
        setShowSubscriptionModal(true);
        return;
      }

      // Get the highest position in the backlog
      const maxPosition = Math.max(
        ...todos.filter(t => t.status === 'backlog').map(t => t.position),
        0
      );

      const { data, error } = await supabase
        .from('todos')
        .insert([
          {
            text: text.trim(),
            status: 'backlog',
            user_id: user.id,
            project_id: selectedProject,
            notes: '',
            priority: false,
            color: null,
            completed_at: null,
            position: maxPosition + 1000, // Add new items at the end
            deadline: null
          }
        ])
        .select()
        .single();

      if (error) {
        if (error.message.includes('Task limit reached')) {
          setShowSubscriptionModal(true);
          return;
        }
        throw error;
      }
      
      setTodos([...todos, data as Todo]);
      
      // Refresh subscription data after adding a sticky note
      fetchSubscription();
    } catch (error) {
      console.error('Error adding sticky note:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    
    await addTodo(input);
    setInput('');
  };

  const handleProjectChange = (projectId: string) => {
    setSelectedProject(projectId);
  };

  const addProject = async (name: string) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('projects')
        .insert([
          {
            name,
            user_id: user.id
          }
        ])
        .select()
        .single();

      if (error) throw error;
      setProjects([...projects, data]);
      setSelectedProject(data.id);
    } catch (error) {
      console.error('Error adding project:', error);
    }
  };

  const updateTodoStatus = async (id: string, status: 'backlog' | 'in_progress' | 'review' | 'testing' | 'done') => {
    if (!user) return;
    
    try {
      const completed_at = status === 'done' ? new Date().toISOString() : null;
      
      const { error } = await supabase
        .from('todos')
        .update({ 
          status,
          completed_at
        })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      setTodos(todos.map(todo =>
        todo.id === id ? { ...todo, status, completed_at } : todo
      ));
    } catch (error) {
      console.error('Error updating sticky note status:', error);
    }
    setActiveMenu(null);
    setMenuPosition(null);
  };

  const togglePriority = async (id: string) => {
    if (!user) return;
    
    try {
      const todo = todos.find(t => t.id === id);
      if (!todo) return;

      const { error } = await supabase
        .from('todos')
        .update({ priority: !todo.priority })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      
      setTodos(prevTodos => {
        const newTodos = prevTodos.map(t =>
          t.id === id ? { ...t, priority: !t.priority } : t
        );
        return [...newTodos].sort((a, b) => {
          if (a.priority === b.priority) {
            return a.position - b.position;
          }
          return a.priority ? -1 : 1;
        });
      });
    } catch (error) {
      console.error('Error updating sticky note priority:', error);
    }
    setActiveMenu(null);
    setMenuPosition(null);
  };

  const updateTodoColor = async (id: string, color: TodoColor) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('todos')
        .update({ color })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      setTodos(todos.map(todo =>
        todo.id === id ? { ...todo, color } : todo
      ));
    } catch (error) {
      console.error('Error updating sticky note color:', error);
    }
    setActiveMenu(null);
    setMenuPosition(null);
  };

  const updateTodoText = async (id: string, newText: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('todos')
        .update({ text: newText })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      setTodos(todos.map(todo =>
        todo.id === id ? { ...todo, text: newText } : todo
      ));
      setEditingTodo(null);
    } catch (error) {
      console.error('Error updating sticky note text:', error);
    }
  };

  const updateTodoNotes = useCallback(async (id: string, notes: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('todos')
        .update({ notes })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      
      setTodos(prevTodos => prevTodos.map(todo =>
        todo.id === id ? { ...todo, notes } : todo
      ));

      if (!hasNotes(notes)) {
        setShowingNotes(null);
      }
    } catch (error) {
      console.error('Error updating sticky note notes:', error);
    }
  }, [user]);

  const startEditing = (todo: Todo) => {
    setEditingTodo({ id: todo.id, text: todo.text });
    setActiveMenu(null);
    setMenuPosition(null);
  };

  const startEditingNotes = (todo: Todo) => {
    setEditingNotes({ id: todo.id, notes: todo.notes || '' });
    setActiveMenu(null);
    setMenuPosition(null);
  };

  const startEditingDeadline = (todo: Todo) => {
    setEditingDeadline({ id: todo.id, deadline: todo.deadline });
    setActiveMenu(null);
    setMenuPosition(null);
  };

  const updateTodoDeadline = async (id: string, deadline: string | null) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('todos')
        .update({ deadline })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      setTodos(prevTodos => prevTodos.map(todo =>
        todo.id === id ? { ...todo, deadline } : todo
      ));

      setEditingDeadline(null);
    } catch (error) {
      console.error('Error updating sticky note deadline:', error);
    }
  };

  const updateTodoProject = async (todoId: string, newProjectId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('todos')
        .update({ project_id: newProjectId })
        .eq('id', todoId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Remove from current view since it moved to another project
      setTodos(todos.filter(todo => todo.id !== todoId));

    } catch (error) {
      console.error('Error moving sticky note to project:', error);
      // Refresh data on error
      fetchTodos();
    }

    setShowMoveToProjectModal(false);
    setMovingTodo(null);
    setActiveMenu(null);
    setMenuPosition(null);
  };

  const startMovingToProject = (todo: Todo) => {
    setMovingTodo(todo);
    setShowMoveToProjectModal(true);
  };

  const downloadNoteAsMarkdown = async (todo: Todo) => {
    setActiveMenu(null);
    try {
      await downloadMarkdown(toMarkdownFileName(todo.text), htmlToMd(todo.notes || ''));
    } catch (error) {
      console.error('Error downloading note:', error);
    }
  };

  const toggleNotes = (todoId: string) => {
    setShowingNotes(showingNotes === todoId ? null : todoId);
  };

  const removeTodo = async (id: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('todos')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      setTodos(todos.filter(todo => todo.id !== id));
      
      // Refresh subscription data after removing a sticky note
      fetchSubscription();
    } catch (error) {
      console.error('Error removing sticky note:', error);
    }
  };

  const archiveCompletedNotes = async () => {
    const notes = todos
      .filter(todo => todo.status === 'done' && hasNotes(todo.notes))
      .map(todo => ({ title: todo.text, content: htmlToMd(todo.notes || '') }));
    if (notes.length === 0) return;
    const projectName = projects.find(p => p.id === selectedProject)?.name || 'notes';
    const date = new Date().toISOString().slice(0, 10);
    try {
      await downloadMarkdownZip(toMarkdownFileName(`${projectName} ${date}`).replace(/\.md$/, '.zip'), notes);
    } catch (error) {
      console.error('Error archiving notes:', error);
    }
  };

  const deleteAllCompletedTodos = async () => {
    if (!user || !selectedProject) return;
    
    try {
      const { error } = await supabase
        .from('todos')
        .delete()
        .eq('user_id', user.id)
        .eq('project_id', selectedProject)
        .eq('status', 'done');

      if (error) throw error;
      
      // Update local state by removing all completed todos
      setTodos(todos.filter(todo => todo.status !== 'done'));
      
      // Refresh subscription data after removing todos
      fetchSubscription();
      
      // Hide confirmation dialog
      setShowDeleteAllConfirm(false);
    } catch (error) {
      console.error('Error deleting completed sticky notes:', error);
    }
  };

  const handleSignOutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setShowLogoutConfirm(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getBackgroundColor = (color: TodoColor) => {
    switch (color) {
      case 'yellow': return 'bg-yellow-50';
      case 'green': return 'bg-green-50';
      case 'blue': return 'bg-blue-50';
      default: return 'bg-white';
    }
  };

  const getDeadlineColor = (deadline: string): string => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const deadlineDate = new Date(deadline);
    deadlineDate.setHours(0, 0, 0, 0);

    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Red: today or past due (0 or negative)
    if (diffDays <= 0) return 'bg-red-500';

    // Orange: tomorrow (1 day)
    if (diffDays === 1) return 'bg-orange-500';

    // Green: future (2+ days)
    return 'bg-green-500';
  };

  const getColorIcon = (color: TodoColor) => {
    switch (color) {
      case 'yellow': return 'text-yellow-500';
      case 'green': return 'text-green-500';
      case 'blue': return 'text-blue-500';
      default: return 'text-gray-400';
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result?.destination || !user || !selectedProject) return;

    const { source, destination, draggableId } = result;
    
    // Get the sticky note that was dragged
    const draggedTodo = todos.find(todo => todo.id === draggableId);
    if (!draggedTodo) return;

    // Check if the sticky note was moved to a different column
    const isColumnChange = source.droppableId !== destination.droppableId;
    
    // Get the new status if the column changed
    const newStatus = isColumnChange 
      ? destination.droppableId as 'backlog' | 'in_progress' | 'review' | 'testing' | 'done'
      : draggedTodo.status;
    
    // Get all sticky notes in the destination column
    const destinationTodos = todos.filter(t => 
      isColumnChange 
        ? t.status === newStatus 
        : t.status === source.droppableId
    );
    
    // Create a copy of the destination sticky notes without the dragged sticky note
    let updatedTodos = isColumnChange
      ? [...destinationTodos]
      : [...destinationTodos.filter(t => t.id !== draggableId)];
    
    // Insert the dragged sticky note at the new position
    updatedTodos.splice(destination.index, 0, {
      ...draggedTodo,
      status: newStatus,
      completed_at: newStatus === 'done' ? new Date().toISOString() : null
    });
    
    // Calculate new positions
    const updatedPositions = updatedTodos.map((item, index) => ({
      ...item,
      position: index * 1000
    }));
    
    // Update local state immediately for smooth UI
    setTodos(prevTodos => {
      // Keep sticky notes from other columns
      const otherTodos = prevTodos.filter(t => 
        isColumnChange 
          ? t.status !== newStatus && t.id !== draggableId
          : t.status !== source.droppableId
      );
      
      // Combine with updated sticky notes
      const newTodos = [...otherTodos, ...updatedPositions];
      
      // Sort by priority and position
      return newTodos.sort((a, b) => {
        if (a.status !== b.status) return 0; // Don't sort across columns
        if (a.priority === b.priority) {
          return a.position - b.position;
        }
        return a.priority ? -1 : 1;
      });
    });
    
    // Update database
    try {
      // If column changed, first update the status
      if (isColumnChange) {
        const { error } = await supabase
          .from('todos')
          .update({ 
            status: newStatus,
            completed_at: newStatus === 'done' ? new Date().toISOString() : null
          })
          .eq('id', draggableId)
          .eq('user_id', user.id);
        
        if (error) throw error;
      }
      
      // Then update positions for all affected sticky notes one by one
      // This approach avoids RLS issues with bulk updates
      for (const item of updatedPositions) {
        const { error } = await supabase
          .from('todos')
          .update({ position: item.position })
          .eq('id', item.id)
          .eq('user_id', user.id);
        
        if (error) {
          console.error('Error updating position for sticky note:', item.id, error);
          throw error;
        }
      }
    } catch (error) {
      console.error('Error updating sticky note positions:', error);
      // Revert to original state on error
      fetchTodos();
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Updated to properly handle the email-based sharing system
  const handleShareTodo = (todo: Todo) => {
    setSendingStickyNote(todo);
    setActiveMenu(null);
    setMenuPosition(null);
  };
  
  const handleInboxClick = () => {
    setShowInbox(true);
  };
  
  const handleOutboxClick = () => {
    setShowOutbox(true);
  };
  
  const handleAcceptSharedTodo = () => {
    fetchTodos();
    fetchInboxCount();
  };

  const getColumnTitle = (status: ColumnType): string => {
    switch(status) {
      case 'backlog': return t('backlog');
      case 'in_progress': return t('inProgress');
      case 'review': return t('review');
      case 'testing': return t('testing');
      case 'done': return t('done');
    }
  };

  const toggleMenu = (e: React.MouseEvent, todoId: string) => {
    e.stopPropagation();
    const isBottom = e.clientY > window.innerHeight / 2;

    if (activeMenu === todoId) {
      setActiveMenu(null);
      setMenuPosition(null);
    } else {
      setActiveMenu(todoId);
      setMenuPosition({ id: todoId, y: e.clientY });
    }
  };

  // Toggle column collapse state for mobile accordion
  const toggleColumnCollapse = (columnId: string) => {
    setCollapsedColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId]
    }));
  };

  const KanbanColumn = ({ title, status, todos, isCollapsed, onToggleCollapse }: {
    title: string,
    status: ColumnType,
    todos: Todo[],
    isCollapsed?: boolean,
    onToggleCollapse?: () => void
  }) => (
    <div className="flex-1 min-w-[300px] bg-gray-50 rounded-lg p-4 flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          {/* Mobile accordion toggle button */}
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="md:hidden text-gray-600 hover:text-gray-800 transition-colors"
              aria-label={isCollapsed ? "Expand column" : "Collapse column"}
            >
              {isCollapsed ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronUp className="h-5 w-5" />
              )}
            </button>
          )}
          <h2 className="text-lg font-semibold text-gray-700">{title}</h2>
        </div>
        {status === 'done' && (
          <div className="flex items-center gap-3">
            {todos.some(todo => todo.status === 'done' && hasNotes(todo.notes)) && (
              <button
                onClick={archiveCompletedNotes}
                className="flex items-center text-xs text-indigo-600 hover:text-indigo-800 transition-colors duration-200"
                title={t('archiveNotesTooltip')}
              >
                <Archive className="h-4 w-4 mr-1" />
                {t('archiveNotes')}
              </button>
            )}
            <button
              onClick={() => setShowDeleteAllConfirm(true)}
              className="flex items-center text-xs text-red-600 hover:text-red-800 transition-colors duration-200"
              title={t('deleteAllTooltip')}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {t('deleteAll')}
            </button>
          </div>
        )}
      </div>
      {/* Conditionally show/hide tasks based on collapse state (only affects mobile, always visible on desktop) */}
      <div className={`flex-1 flex-col ${isCollapsed ? 'hidden md:flex' : 'flex'}`}>
        <Droppable droppableId={status}>
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="space-y-2 min-h-[200px] flex-1"
            >
              {todos.filter(todo => todo.status === status).map((todo, index) => (
              <Draggable key={todo.id} draggableId={todo.id} index={index}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={`${getBackgroundColor(todo.color)} rounded-lg shadow-sm relative group ${todo.deadline ? 'p-4 pl-12' : 'p-4'}`}
                  >
                    {/* Deadline indicator */}
                    {todo.deadline && (
                      <div className={`absolute -top-2 -left-2 w-12 h-12 ${getDeadlineColor(todo.deadline)} rounded-full flex flex-col items-center justify-center text-white text-xs font-bold shadow-lg border-2 border-white`}>
                        <span className="text-sm">{new Date(todo.deadline).getDate()}</span>
                        <span className="text-[10px]">{new Date(todo.deadline).toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      {editingTodo?.id === todo.id ? (
                        <TodoEditText
                          key={todo.id}
                          initialValue={editingTodo.text}
                          onSave={(value) => updateTodoText(todo.id, value)}
                        />
                      ) : (
                        <div className="flex-1 flex items-center space-x-2">
                          <span 
                            className={`text-gray-700 cursor-pointer hover:text-gray-900 ${todo.priority ? 'font-bold' : ''}`}
                            onDoubleClick={() => startEditing(todo)}
                            title="Double-click to rename"
                          >
                            {todo.priority && <Star className="h-4 w-4 inline-block mr-1 text-yellow-500" />}
                            {todo.text}
                          </span>
                          {hasNotes(todo.notes) && (
                            <button
                              onClick={() => toggleNotes(todo.id)}
                              className="text-gray-400 hover:text-gray-600"
                              title="Show note"
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      )}
                      <div className="flex items-center space-x-2">
                        <button
                          ref={activeMenu === todo.id ? buttonRef : null}
                          onClick={(e) => toggleMenu(e, todo.id)}
                          className="text-gray-400 hover:text-gray-600 focus:outline-none"
                        >
                          <MoreVertical className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm({ id: todo.id, text: todo.text })}
                          className="text-red-500 hover:text-red-600 focus:outline-none"
                        >
                          <XCircle className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    {showingNotes === todo.id && hasNotes(todo.notes) && (
                      <div
                        className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-700 cursor-pointer hover:bg-gray-100 max-h-[240px] overflow-y-auto prose prose-sm max-w-none"
                        onDoubleClick={() => startEditingNotes(todo)}
                        title="Double-click to edit"
                      >
                        <NotesMarkdown notes={todo.notes} />
                      </div>
                    )}
                    
                    {activeMenu === todo.id && (
                      <div 
                        ref={menuRef}
                        className={`absolute right-0 ${
                          menuPosition?.id === todo.id && menuPosition.y > window.innerHeight / 2
                            ? 'bottom-12' // Position above the button when in bottom half
                            : 'top-12'    // Default position below the button
                        } bg-white rounded-lg shadow-lg py-2 z-40 min-w-[160px]`}
                      >
                        <button
                          onClick={() => startEditingNotes(todo)}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <FileText className="h-4 w-4 inline-block mr-2" />
                          {t('note')}
                        </button>
                        {hasNotes(todo.notes) && (
                          <button
                            onClick={() => downloadNoteAsMarkdown(todo)}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <Download className="h-4 w-4 inline-block mr-2" />
                            {t('downloadNoteMarkdown')}
                          </button>
                        )}
                        <button
                          onClick={() => startEditingDeadline(todo)}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Calendar className="h-4 w-4 inline-block mr-2" />
                          {todo.deadline ? t('changeDeadline') : t('setDeadline')}
                        </button>
                        <button
                          onClick={() => togglePriority(todo.id)}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Star className={`h-4 w-4 inline-block mr-2 ${todo.priority ? 'text-yellow-500' : ''}`} />
                          {todo.priority ? t('priorityOff') : t('priorityOn')}
                        </button>
                        <button
                          onClick={() => handleShareTodo(todo)}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Send className="h-4 w-4 inline-block mr-2" />
                          {t('sendStickyNote')}
                        </button>
                        <button
                          onClick={() => startMovingToProject(todo)}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        >
                          <FolderOpen className="h-4 w-4 inline-block mr-2" />
                          {t('moveToProject')}
                        </button>
                        <div className="px-4 py-2 text-sm text-gray-700">
                          <div className="mb-1">{t('color')}</div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => updateTodoColor(todo.id, null)}
                              className="p-1 hover:bg-gray-100 rounded"
                              title={t('noColor')}
                            >
                              <Circle className="h-4 w-4 text-gray-400" />
                            </button>
                            <button
                              onClick={() => updateTodoColor(todo.id, 'yellow')}
                              className="p-1 hover:bg-gray-100 rounded"
                              title={t('yellow')}
                            >
                              <Circle className="h-4 w-4 text-yellow-500" fill="currentColor" />
                            </button>
                            <button
                              onClick={() => updateTodoColor(todo.id, 'green')}
                              className="p-1 hover:bg-gray-100 rounded"
                              title={t('green')}
                            >
                              <Circle className="h-4 w-4 text-green-500" fill="currentColor" />
                            </button>
                            <button
                              onClick={() => updateTodoColor(todo.id, 'blue')}
                              className="p-1 hover:bg-gray-100 rounded"
                              title={t('blue')}
                            >
                              <Circle className="h-4 w-4 text-blue-500" fill="currentColor" />
                            </button>
                          </div>
                        </div>
                        <div className="border-t my-2"></div>
                        <button
                          onClick={() => updateTodoStatus(todo.id, 'backlog')}
                          className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                            todo.status === 'backlog' ? 'text-indigo-600 font-medium' : 'text-gray-700'
                          }`}
                          disabled={todo.status === 'backlog'}
                        >
                          {t('backlog')}
                        </button>
                        <button
                          onClick={() => updateTodoStatus(todo.id, 'in_progress')}
                          className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                            todo.status === 'in_progress' ? 'text-indigo-600 font-medium' : 'text-gray-700'
                          }`}
                          disabled={todo.status === 'in_progress'}
                        >
                          {t('inProgress')}
                        </button>
                        {columnSettings.review && (
                          <button
                            onClick={() => updateTodoStatus(todo.id, 'review')}
                            className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                              todo.status === 'review' ? 'text-indigo-600 font-medium' : 'text-gray-700'
                            }`}
                            disabled={todo.status === 'review'}
                          >
                            {t('review')}
                          </button>
                        )}
                        {columnSettings.testing && (
                          <button
                            onClick={() => updateTodoStatus(todo.id, 'testing')}
                            className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                              todo.status === 'testing' ? 'text-indigo-600 font-medium' : 'text-gray-700'
                            }`}
                            disabled={todo.status === 'testing'}
                          >
                            {t('testing')}
                          </button>
                        )}
                        <button
                          onClick={() => updateTodoStatus(todo.id, 'done')}
                          className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                            todo.status === 'done' ? 'text-indigo-600 font-medium' : 'text-gray-700'
                          }`}
                          disabled={todo.status === 'done'}
                        >
                          {t('done')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </Draggable>
            ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    </div>
  );

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
        <div className="text-lg text-gray-600">{t('loading')}</div>
        <CookieConsent />
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <LandingPage />
        <CookieConsent />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-indigo-100 flex flex-col">
      <div className="flex flex-col">
        <div className="bg-white shadow-xl flex-1 flex flex-col">
          {/* Sticky top bar: header + project selector + add-task form */}
          <div className="sticky top-0 z-30 bg-white">
          {/* Header */}
          <div className="bg-indigo-600 px-4 md:px-6 py-3 md:py-4 flex-shrink-0">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              {/* Logo and hamburger for mobile */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <ClipboardList className="h-6 w-6 text-indigo-100" />
                  <h1 className="text-xl md:text-2xl font-bold text-white">Simple Stickers</h1>
                </div>
                <button 
                  className="lg:hidden text-indigo-100 hover:text-white focus:outline-none" 
                  onClick={toggleMobileMenu}
                >
                  {mobileMenuOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>
              </div>
              
              {/* Navigation items - hidden on mobile unless menu is open */}
              <div className={`${
                mobileMenuOpen ? 'flex' : 'hidden'
              } lg:flex flex-col lg:flex-row lg:items-center mt-4 lg:mt-0 space-y-3 lg:space-y-0 lg:space-x-4`}>
                {/* Subscription badge */}
                {subscription && subscription.subscriptionTier === 'premium' && (
                  <div className="flex items-center bg-indigo-700 text-white text-sm px-3 py-1 rounded-full">
                    <CreditCard className="h-4 w-4 mr-1" />
                    Premium
                  </div>
                )}
                
                {/* AI Usage Indicator */}
                <div className="order-1 lg:order-none">
                  <AIUsageIndicator onUpgrade={() => setShowSubscriptionModal(true)} />
                </div>
                
                {/* Action buttons */}
                <div className="flex items-center space-x-3 md:space-x-4">
                  {/* Columns Settings Button */}
                  <ColumnSettingsButton onClick={() => setShowColumnSettings(true)} />
                  
                  {/* Inbox Button */}
                  <button
                    onClick={handleInboxClick}
                    className="relative text-indigo-100 hover:text-white focus:outline-none"
                    title="Shared Sticky Notes Inbox"
                  >
                    <Inbox className="h-5 w-5" />
                    {inboxCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {inboxCount}
                      </span>
                    )}
                  </button>
                  
                  {/* Outbox Button */}
                  <button
                    onClick={handleOutboxClick}
                    className="text-indigo-100 hover:text-white focus:outline-none"
                    title="Sent Sticky Notes"
                  >
                    <Share2 className="h-5 w-5" />
                  </button>
                  
                  <button
                    onClick={() => setShowAccountSettings(true)}
                    className="text-indigo-100 hover:text-white focus:outline-none"
                    title="Account Settings"
                  >
                    <Settings className="h-5 w-5" />
                  </button>
                  <HelpButton className="text-indigo-100 hover:text-white focus:outline-none" />
                  {user && (
                    <>
                      <span className="text-indigo-100 hidden md:inline">{user.email}</span>
                      <button
                        onClick={handleSignOutClick}
                        className="text-indigo-100 hover:text-white focus:outline-none"
                        title="Sign out"
                      >
                        <LogOut className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Project Selector */}
          <div className="px-4 md:px-6 pt-3 md:pt-4 pb-1">
            <ProjectSelector
              projects={projects}
              selectedProject={selectedProject}
              onProjectChange={handleProjectChange}
              onAddProject={addProject}
              onRefresh={async () => { await fetchProjects(); await fetchTodos(); }}
            />
          </div>

          {/* Subscription Banner */}
          {subscription && showSubscriptionBanner && (
            <div className="px-4 md:px-6">
              <SubscriptionBanner
                subscription={subscription}
                polarCustomer={polarCustomer}
                onUpgrade={() => setShowSubscriptionModal(true)}
                onManageSubscription={handleManageSubscription}
                onDismiss={() => setShowSubscriptionBanner(false)}
              />
            </div>
          )}

          {/* Add Todo Form */}
          <form onSubmit={handleSubmit} className="px-4 md:px-6 py-2 md:py-3 border-b">
            <div className="flex space-x-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={selectedProject ? t('addStickyNote') : t('selectProject')}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 pr-12"
                  disabled={!selectedProject}
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  <AISuggestions
                    input={input}
                    onSuggestionClick={async (suggestion) => {
                      await addTodo(suggestion);
                    }}
                    onClearInput={() => setInput('')}
                  />
                </div>
              </div>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!selectedProject}
              >
                <PlusCircle className="h-5 w-5" />
              </button>
            </div>
          </form>
          </div>
          {/* End sticky top bar */}

          {/* Kanban Board */}
          {loading ? (
            <div className="p-4 md:p-6 text-center text-gray-500">
              {t('loadingStickyNotes')}
            </div>
          ) : projects.length === 0 ? (
            <div className="p-4 md:p-6 text-center text-gray-500 flex items-center justify-center gap-2">
              {t('getStartedMessage')} <PlusCircle className="h-5 w-5 inline-block text-indigo-600" />
            </div>
          ) : !selectedProject ? (
            <div className="p-4 md:p-6 text-center text-gray-500">
              {t('selectProjectMessage')}
            </div>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <div className="p-4 md:p-6 flex-1 overflow-visible">
                <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 h-full">
                  {/* Show columns based on settings */}
                  {columnSettings.backlog && (
                    <KanbanColumn
                      title={t('backlog')}
                      status="backlog"
                      todos={todos}
                      isCollapsed={collapsedColumns['backlog']}
                      onToggleCollapse={() => toggleColumnCollapse('backlog')}
                    />
                  )}
                  {columnSettings.in_progress && (
                    <KanbanColumn
                      title={t('inProgress')}
                      status="in_progress"
                      todos={todos}
                      isCollapsed={collapsedColumns['in_progress']}
                      onToggleCollapse={() => toggleColumnCollapse('in_progress')}
                    />
                  )}
                  {columnSettings.review && (
                    <KanbanColumn
                      title={t('review')}
                      status="review"
                      todos={todos}
                      isCollapsed={collapsedColumns['review']}
                      onToggleCollapse={() => toggleColumnCollapse('review')}
                    />
                  )}
                  {columnSettings.testing && (
                    <KanbanColumn
                      title={t('testing')}
                      status="testing"
                      todos={todos}
                      isCollapsed={collapsedColumns['testing']}
                      onToggleCollapse={() => toggleColumnCollapse('testing')}
                    />
                  )}
                  {columnSettings.done && (
                    <KanbanColumn
                      title={t('done')}
                      status="done"
                      todos={todos}
                      isCollapsed={collapsedColumns['done']}
                      onToggleCollapse={() => toggleColumnCollapse('done')}
                    />
                  )}
                </div>
              </div>
            </DragDropContext>
          )}
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {editingNotes && (
        <NotesEditor
          content={editingNotes.notes}
          onClose={() => setEditingNotes(null)}
          onSave={(content) => {
            updateTodoNotes(editingNotes.id, content);
            setEditingNotes(null);
          }}
        />
      )}

      {editingDeadline && (
        <SetDeadlineModal
          isOpen={true}
          onClose={() => setEditingDeadline(null)}
          onSave={(deadline) => updateTodoDeadline(editingDeadline.id, deadline)}
          currentDeadline={editingDeadline.deadline}
          todoText={todos.find(t => t.id === editingDeadline.id)?.text || ''}
        />
      )}

      <MoveToProjectModal
        isOpen={showMoveToProjectModal}
        onClose={() => {
          setShowMoveToProjectModal(false);
          setMovingTodo(null);
        }}
        onSave={(newProjectId) => {
          if (movingTodo) {
            updateTodoProject(movingTodo.id, newProjectId);
          }
        }}
        todo={movingTodo}
        projects={projects}
        currentProjectId={selectedProject || ''}
      />

      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('logoutConfirmTitle')}</h3>
              <p className="mt-2 text-sm text-gray-500">
                {t('logoutConfirmMessage')}
              </p>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleSignOut}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {t('logOut')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('deleteStickyNote')}</h3>
              <p className="mt-2 text-sm text-gray-700 font-medium">
                "{showDeleteConfirm.text}"
              </p>
              <p className="mt-2 text-sm text-gray-500">
                {t('deleteStickyNoteMessage')}
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-6 py-3 text-gray-700 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 text-base"
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => {
                  removeTodo(showDeleteConfirm.id);
                  setShowDeleteConfirm(null);
                }}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-base"
              >
                {t('deleteButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteAllConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('deleteAllCompleted')}</h3>
              <p className="mt-2 text-sm text-gray-500">
                {t('deleteAllCompletedMessage')}
              </p>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowDeleteAllConfirm(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                {t('cancel')}
              </button>
              <button
                onClick={deleteAllCompletedTodos}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {t('deleteAllButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSubscriptionModal && (
        <SubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          onSuccess={handleSubscriptionSuccess}
        />
      )}

      {showAccountSettings && (
        <AccountSettings
          isOpen={showAccountSettings}
          onClose={() => setShowAccountSettings(false)}
        />
      )}
      
      {sendingStickyNote && (
        <SendStickyNoteModal
          isOpen={true}
          onClose={() => setSendingStickyNote(null)}
          todoId={sendingStickyNote.id}
          todoText={sendingStickyNote.text}
          todoNotes={sendingStickyNote.notes}
          todoDeadline={sendingStickyNote.deadline}
        />
      )}
      
      {showInbox && (
        <SharedInbox
          isOpen={true}
          onClose={() => setShowInbox(false)}
          projects={projects}
          onAccept={handleAcceptSharedTodo}
        />
      )}
      
      {showOutbox && (
        <SharedOutbox
          isOpen={true}
          onClose={() => setShowOutbox(false)}
        />
      )}
      
      {showColumnSettings && (
        <ColumnSettings
          isOpen={true}
          onClose={() => setShowColumnSettings(false)}
          onColumnsChange={handleColumnSettingsChange}
          initialSettings={columnSettings}
        />
      )}

      {/* Cookie Consent Banner */}
      <CookieConsent />

      {/* Sentry Test Component (Development only) */}
      {/* <SentryTest /> */}
    </div>
  );
}

export default App;